/**
 * Global 401 handler — bounce to /auth/login on any expired-session response.
 *
 * The session cookie has a 15-minute TTL with no sliding refresh. When a
 * user signs in, walks away for coffee, and comes back to click a button,
 * the shell API returns 401. The original UX was: stuck on a dead "status
 * 401" toast with no recovery. The user has to know to re-sign-in.
 *
 * This handler patches `window.fetch` once at boot. On any 401 response
 * from a `/api/shell/*` endpoint (except the login + session endpoints
 * themselves), the cockpit hard-navigates to /auth/login with:
 *   - `?reason=expired` so the login page can show "Your session ended"
 *   - `?from=<current-path>` so login can return the user to where they
 *     were after a successful re-auth.
 *
 * The 401 response is still returned to the caller in case a fetch site
 * has cleanup to do, but since we navigate immediately the rest of the
 * promise chain typically doesn't run.
 *
 * No-login pages (`/l/<secret>`, secure links; `/f/<secret>`, Forms) never
 * bounce: the visitor has no staff session to renew, and `?from=` would
 * carry the link's secret into the login page's address (SL28).
 */

const SHELL_API_PREFIX = "/api/shell/";

// Endpoints that should NOT trigger a bounce — calling these IS the
// recovery path, so bouncing on their 401 would loop.
const BYPASS_PATHS = [
  "/api/shell/auth/login",
  "/api/shell/auth/session",
  "/api/shell/auth/logout",
  "/api/shell/auth/tenants",
  "/api/shell/auth/select-tenant",
];

function shouldBounce(url: string): boolean {
  if (!url.includes(SHELL_API_PREFIX)) return false;
  return !BYPASS_PATHS.some((p) => url.includes(p));
}

// Pages a visitor opens without logging in; the path holds the link's secret.
const NO_LOGIN_PREFIXES = ["/l/", "/f/"];

function onNoLoginPage(pathname: string): boolean {
  return NO_LOGIN_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function urlFromArgs(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

let installed = false;

export function install401Handler(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const res = await originalFetch(input, init);

    if (res.status === 401) {
      const url = urlFromArgs(input);
      const pathname = window.location.pathname;
      const onLogin = pathname.startsWith("/auth/");

      if (shouldBounce(url) && !onLogin && !onNoLoginPage(pathname)) {
        const from = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        // Hard navigate — the cockpit's auth store + router state are
        // about to be irrelevant anyway. A full reload also clears any
        // in-memory state tied to the dead session.
        window.location.href = `/auth/login?reason=expired&from=${from}`;
      }
    }

    return res;
  };
}
